import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'download');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const activeCategory = useMemo(
    () => categories.find((c) => c.slug === activeSlug),
    [activeSlug]
  );

  const categoryArticles = useMemo(
    () => articles.filter((a) => a.category === activeSlug),
    [activeSlug]
  );

  const catName = activeCategory?.name || '分类';
  const catDesc = activeCategory?.description || '';

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${catName} - 蘑菇视频`}
        description={catDesc}
        keywords={`蘑菇视频app,${catName},蘑菇视频app下载,短视频娱乐平台`}
        canonicalUrl={`/category/${activeSlug}`}
      />
      <Navbar />

      <main className="pt-24 pb-16 md:pb-20 min-h-screen bg-background-50">
        <div className="container-page">
          <div className="mb-8">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: catName },
              ]}
            />
          </div>

          <div className="mb-10">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
              {catName}
            </h1>
            <p className="text-foreground-500 text-base">{catDesc}</p>
          </div>

          <div className="flex flex-wrap gap-2 mb-10">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categoryArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.slug}`}
                className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
              >
                <div className="w-full h-48 overflow-hidden">
                  <img
                    src={article.coverImage}
                    alt={article.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 rounded text-xs bg-accent-100 text-accent-800">
                      {article.categoryName}
                    </span>
                    <span className="text-xs text-foreground-400">{article.publishDate}</span>
                    <span className="text-xs text-foreground-400 ml-auto">
                      <i className="ri-time-line mr-1"></i>
                      {article.readingTime}分钟
                    </span>
                  </div>
                  <h2 className="text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2 font-heading">
                    {article.title}
                  </h2>
                  <p className="text-sm text-foreground-500 line-clamp-2">{article.excerpt}</p>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {article.tags.map((tag) => (
                      <span key={tag} className="px-2 py-0.5 rounded text-xs bg-secondary-100 text-secondary-800">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {categoryArticles.length === 0 && (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-inbox-line text-3xl text-foreground-300"></i>
              </div>
              <p className="text-foreground-500 text-base">该分类暂无文章</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}